import { useEffect, useMemo, useRef } from 'react';
import { Configuration } from '@app/types/sdk';
import type { ConfigurationClass, Middleware } from '@app/types/sdk';
import { useConfig } from '@config/provider';
import { buildAccessTokenProvider, readStoredTokens, type PersistedToken } from '@app/api/token';
import { useAuthSession } from '@app/context/auth-session';
import { createTimedFetch } from '@app/utils/timed-fetch';

const REFRESH_THRESHOLD_MS = 60 * 1000;
const RETRY_FLAG = '__reap401Retried';

const shouldRefreshToken = (tokens: PersistedToken | null): boolean => {
  if (!tokens?.refreshToken) {
    return false;
  }
  if (typeof tokens.expiresAt !== 'number') {
    return false;
  }
  return Date.now() >= tokens.expiresAt - REFRESH_THRESHOLD_MS;
};

export const useApiConfiguration = (): ConfigurationClass => {
  const { apiBaseUrl } = useConfig();
  const { tokens, refreshTokens } = useAuthSession();
  const accessTokenResolver = useMemo(() => buildAccessTokenProvider(), []);
  const timedFetch = useMemo(() => createTimedFetch(), []);
  const tokensRef = useRef(tokens);
  const refreshTokensRef = useRef(refreshTokens);

  useEffect(() => {
    tokensRef.current = tokens;
  }, [tokens]);

  useEffect(() => {
    refreshTokensRef.current = refreshTokens;
  }, [refreshTokens]);

  const basePath = useMemo(() => {
    if (/^https?:\/\//i.test(apiBaseUrl)) {
      return apiBaseUrl.replace(/\/+$/, '');
    }

    const normalized = apiBaseUrl.startsWith('/') ? apiBaseUrl : `/${apiBaseUrl}`;
    const origin = typeof window !== 'undefined' ? window.location.origin : '';
    return `${origin}${normalized}`.replace(/\/+$/, '');
  }, [apiBaseUrl]);

  const middleware = useMemo<Middleware[]>(() => {
    const authMiddleware: Middleware = {
      pre: async (context) => {
        const currentTokens = readStoredTokens();
        tokensRef.current = currentTokens;
        let accessToken = currentTokens?.accessToken ?? null;
        if (shouldRefreshToken(currentTokens)) {
          const refreshed = await refreshTokensRef.current();
          accessToken = refreshed?.accessToken ?? accessToken;
        }

        if (!accessToken) {
          return context;
        }

        const headers = new Headers((context.init.headers ?? {}) as HeadersInit);
        headers.set('Authorization', `Bearer ${accessToken}`);
        return {
          url: context.url,
          init: {
            ...context.init,
            headers
          }
        };
      },
      post: async (context) => {
        if (context.response.status !== 401) {
          return;
        }

        const initWithRetryFlag = context.init as Record<string, unknown> & { [RETRY_FLAG]?: boolean };
        if (initWithRetryFlag?.[RETRY_FLAG]) {
          return;
        }

        const refreshed = await refreshTokensRef.current();
        if (!refreshed) {
          return;
        }

        const nextHeaders = new Headers((context.init.headers ?? {}) as HeadersInit);
        nextHeaders.set('Authorization', `Bearer ${refreshed.accessToken}`);
        const nextInit = {
          ...context.init,
          headers: nextHeaders
        } as RequestInit & { [RETRY_FLAG]?: boolean };
        nextInit[RETRY_FLAG] = true;

        return context.fetch(context.url, nextInit);
      }
    };
    return [authMiddleware];
  }, []);

  return useMemo(
    () =>
      new Configuration({
        basePath,
        accessToken: accessTokenResolver,
        fetchApi: timedFetch,
        middleware
      }),
    [basePath, accessTokenResolver, middleware, timedFetch]
  );
};
